import { MonthYearPeriod, roundToNearest, USER_ROUNDING_NUM_DECIMALS } from '@common/neo.tax';
import { useEffect, useState } from 'react';

import { Contributor } from '@neo.tax/asc/hooks/useContributor';
import {
  AscAllocationProjectDetails,
  AscContributorProjectAllocation,
  ContributorAllocationsInPeriod,
} from '@neo.tax/asc/types';
import { updateNonProjectTimePercent, updateProjectAllocationPercent } from '@neo.tax/shared';

import {
  getAscProjectIdToAscProjectPerContributorInPeriod,
  initializeAllocationsInPeriod,
} from '../contributor-allocation.utils';

interface UseContributorAllocationModalStateParams {
  isOpen: boolean;
  contributor: Contributor;
  ascProjects: AscAllocationProjectDetails[];
  period: MonthYearPeriod;
  allAscContributorProjectAllocations: AscContributorProjectAllocation[] | null;
  allAscContributorProjectAllocationsLoading: boolean;
  filteredAscContributorProjectAllocations: AscContributorProjectAllocation[] | null;
  filteredAscContributorProjectAllocationsLoading: boolean;
  searchKeyword: string | null;
}

interface UseContributorAllocationModalStateReturn {
  modalAllocationPercents: ContributorAllocationsInPeriod;
  updateNonProjectPercent: (nonProjectPercent: number) => void;
  updateProjectAllocation: (params: { id: string; allocationPercent: number }) => void;
}

/**
 * Manages allocation state for the contributor allocation modal.
 *
 * Handles:
 * - Initial state setup with default or loaded allocations
 * - Reinitialization when modal opens and data loads
 * - State reset when modal closes
 * - Adding newly searched projects to the allocation state
 * - Providing update functions for non-project and project allocations
 */
export const useContributorAllocationModalState = ({
  allAscContributorProjectAllocations,
  allAscContributorProjectAllocationsLoading,
  ascProjects,
  contributor,
  filteredAscContributorProjectAllocations,
  filteredAscContributorProjectAllocationsLoading,
  isOpen,
  period,
  searchKeyword,
}: UseContributorAllocationModalStateParams): UseContributorAllocationModalStateReturn => {
  const [modalAllocationPercents, setModalAllocationPercents] = useState<ContributorAllocationsInPeriod>(() => {
    if (!allAscContributorProjectAllocations) {
      return {
        nonProjectPercent: {
          allocationPercent: 100,
          originalAllocationPercent: 100,
          lastAllocationPercentOnProjectPercentUpdate: 100,
        },
        projectPercents: {
          projectIdToProjectAllocations: new Map(),
          selectedIdToProjectAllocations: new Map(),
        },
      };
    }
    return initializeAllocationsInPeriod({
      ascContributorProjectAllocations: allAscContributorProjectAllocations,
      contributor,
      ascProjects,
      period,
    });
  });

  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize modal allocation percents when modal opens and data loads
  useEffect(() => {
    if (
      isOpen &&
      allAscContributorProjectAllocations &&
      !allAscContributorProjectAllocationsLoading &&
      !hasInitialized
    ) {
      setModalAllocationPercents(
        initializeAllocationsInPeriod({
          ascContributorProjectAllocations: allAscContributorProjectAllocations,
          contributor,
          ascProjects,
          period,
        }),
      );
      setHasInitialized(true);
    }
  }, [
    isOpen,
    allAscContributorProjectAllocations,
    allAscContributorProjectAllocationsLoading,
    hasInitialized,
    contributor,
    ascProjects,
    period,
  ]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  /**
   * Handle search results: Add newly searched projects to the modal state
   *
   * When a user searches for projects (via searchKeyword), this effect:
   * 1. Takes the filtered search results from the GraphQL query
   * 2. Adds any NEW projects to the modal's allocation state
   * 3. Skips projects already in state (prevents duplicates)
   * 4. Initializes each new project with its current allocation percentage
   * 5. Includes month-level breakdown data for accurate calculations
   *
   * Why this is needed:
   * - The modal initially loads with the contributor's current allocations
   * - Search allows users to add MORE projects to allocate time to
   * - Each searched project needs month-level data for the breakdown calculations
   *
   * Note: We only update state if there are actual changes to avoid unnecessary re-renders
   */
  useEffect(() => {
    if (
      isOpen &&
      searchKeyword &&
      filteredAscContributorProjectAllocations &&
      !filteredAscContributorProjectAllocationsLoading
    ) {
      setModalAllocationPercents((currentState) => {
        const newState = { ...currentState };
        const projectMap = newState.projectPercents.projectIdToProjectAllocations;
        let hasChanges = false;

        // Pre-fetch month-level data for ALL contributor projects in this period.
        // This is used to get the breakdown of how allocations vary by month.
        const projectIdToMonthData = getAscProjectIdToAscProjectPerContributorInPeriod(contributor, period);

        // Process each project from the search results
        for (const allocation of filteredAscContributorProjectAllocations) {
          // Skip if this project is already in the modal (prevents duplicates)
          if (!projectMap.has(allocation.ascProjectId)) {
            // Round the allocation percentage consistently with our rounding strategy
            const allocationPercent = roundToNearest(allocation.allocationPercentAvg, USER_ROUNDING_NUM_DECIMALS);

            // Get month-by-month breakdown for this specific project.
            // This shows how the contributor's time on this project varies across months.
            const projectMonthData = projectIdToMonthData.get(allocation.ascProjectId);
            const monthsPerContributor =
              projectMonthData?.ascProjectPerContributor.ascProjectMonthsPerContributor || [];

            // Create the allocation entry with all required data
            const contributorProjectAllocation = {
              projectId: allocation.ascProjectId,
              projectName: allocation.ascProjectName.trim(),
              allocationPercent,
              originalAllocationPercent: allocationPercent,
              lastAllocationPercentOnProjectPercentUpdate: allocationPercent,
              projectMonthsPerContributor: monthsPerContributor,
              capitalizationStatus: allocation.capitalizationStatus,
              capitalizationStartDate: allocation.capitalizationStartDate
                ? new Date(allocation.capitalizationStartDate)
                : null,
              capitalizationEndDate: allocation.capitalizationEndDate
                ? new Date(allocation.capitalizationEndDate)
                : null,
            };

            // Add to state
            projectMap.set(allocation.ascProjectId, contributorProjectAllocation);
            hasChanges = true;
          }
        }

        // Only trigger re-render if we actually added new projects
        return hasChanges ? newState : currentState;
      });
    }
  }, [
    isOpen,
    searchKeyword,
    filteredAscContributorProjectAllocations,
    filteredAscContributorProjectAllocationsLoading,
    contributor,
    period,
  ]);

  const updateNonProjectPercent = (nonProjectPercent: number): void => {
    setModalAllocationPercents((currentAllocationPercents) =>
      updateNonProjectTimePercent({
        allocationsInPeriod: currentAllocationPercents,
        newAllocationPercent: nonProjectPercent,
      }),
    );
  };

  const updateProjectAllocation = ({
    allocationPercent,
    id: projectId,
  }: {
    id: string;
    allocationPercent: number;
  }): void => {
    setModalAllocationPercents((currentAllocationPercents) =>
      updateProjectAllocationPercent({
        allocationsInPeriod: currentAllocationPercents,
        newAllocationPercent: allocationPercent,
        projectId: projectId,
      }),
    );
  };

  return {
    modalAllocationPercents,
    updateNonProjectPercent,
    updateProjectAllocation,
  };
};
